'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  industry: string;
  employees: string;
  service: string;
  budget: string;
  timeline: string;
  message: string;
  preferredContact: string;
  gdprConsent: boolean;
}

interface ContactFormWizardProps {
  selectedMethod: string;
  onClose: () => void;
  className?: string;
}

const ContactFormWizard = ({ selectedMethod, onClose, className = '' }: ContactFormWizardProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    industry: '',
    employees: '',
    service: '',
    budget: '',
    timeline: '',
    message: '',
    preferredContact: 'email',
    gdprConsent: false,
  });
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  React.useEffect(() => {
    setIsHydrated(true);
  }, []);

  const totalSteps = 4;

  const industries = [
    'E-commerce',
    'Productie',
    'Gezondheidszorg',
    'Financiële diensten',
    'Retail',
    'Logistiek',
    'Horeca',
    'Vastgoed',
    'Marketing & Reclame',
    'Anders',
  ];

  const services = [
    'AI Chatbots',
    'Procesautomatisering',
    'Voorspellende analyses',
    'Content generatie',
    'Klantenservice AI',
    'Data-analyse',
    'Custom AI-oplossing',
    'Nog niet zeker',
  ];

  const budgetRanges = [
    '< €5.000',
    '€5.000 - €15.000',
    '€15.000 - €30.000',
    '€30.000 - €50.000',
    '> €50.000',
    'Nog niet bepaald',
  ];

  const timelines = [
    'Zo snel mogelijk',
    'Binnen 1 maand',
    '1-3 maanden',
    '3-6 maanden',
    '> 6 maanden',
    'Nog niet bepaald',
  ];

  const handleInputChange = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Partial<FormData> = {};

    if (step === 1) {
      if (!formData.firstName.trim()) newErrors.firstName = 'Voornaam is verplicht';
      if (!formData.lastName.trim()) newErrors.lastName = 'Achternaam is verplicht';
      if (!formData.email.trim()) {
        newErrors.email = 'E-mail is verplicht';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Ongeldig e-mailadres';
      }
      if (!formData.phone.trim()) newErrors.phone = 'Telefoonnummer is verplicht';
    }

    if (step === 2) {
      if (!formData.company.trim()) newErrors.company = 'Bedrijfsnaam is verplicht';
      if (!formData.industry) newErrors.industry = 'Selecteer een branche';
      if (!formData.employees) newErrors.employees = 'Selecteer aantal medewerkers';
    }

    if (step === 3) {
      if (!formData.service) newErrors.service = 'Selecteer een dienst';
      if (!formData.budget) newErrors.budget = 'Selecteer een budget';
      if (!formData.timeline) newErrors.timeline = 'Selecteer een tijdlijn';
    }

    if (step === 4) {
      if (!formData.message.trim()) newErrors.message = 'Bericht is verplicht';
      if (!formData.gdprConsent) newErrors.gdprConsent = 'U moet akkoord gaan met de privacyverklaring' as any;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
    }
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setIsSubmitting(true);

    try {
      // Submit to Supabase leads table
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();
      
      const { error } = await supabase.from('leads').insert({
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        industry: formData.industry,
        employees: formData.employees,
        service: formData.service,
        budget: formData.budget,
        timeline: formData.timeline,
        message: formData.message,
        preferred_contact: formData.preferredContact,
        source: 'contact_form',
        status: 'new',
      });

      if (error) throw error;

      setIsSubmitting(false);
      setSubmitSuccess(true);

      // Reset after 3 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
        onClose();
      }, 3000);
    } catch (error) {
      console.error('Error submitting lead:', error);
      setIsSubmitting(false);
      alert('Failed to submit form. Please try again.');
    }
  };

  if (!isHydrated) {
    return (
      <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center">
        <div className="glassmorphic rounded-2xl p-8 max-w-2xl w-full mx-4">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
            <div className="space-y-3 mt-8">
              <div className="h-12 bg-muted rounded"></div>
              <div className="h-12 bg-muted rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (submitSuccess) {
    return (
      <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="glassmorphic rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircleIcon" size={32} className="text-success" />
          </div>
          <h3 className="text-2xl font-bold text-foreground mb-2">Bedankt voor uw aanvraag!</h3>
          <p className="text-muted-foreground mb-4">
            We hebben uw bericht ontvangen en nemen binnen 2 uur contact met u op.
          </p>
          <p className="text-sm text-muted-foreground">
            U ontvangt een bevestigingsmail op <span className="text-primary">{formData.email}</span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="glassmorphic rounded-2xl p-6 sm:p-8 max-w-2xl w-full my-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Contactformulier</h2>
            <p className="text-sm text-muted-foreground">
              Stap {currentStep} van {totalSteps}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-lg bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors duration-300"
          >
            <Icon name="XMarkIcon" size={20} className="text-foreground" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {Array.from({ length: totalSteps }).map((_, index) => (
              <div
                key={index}
                className={`flex-1 h-2 rounded-full mx-1 transition-all duration-300 ${
                  index + 1 <= currentStep ? 'bg-primary' : 'bg-muted'
                }`}
              ></div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="space-y-6">
          {currentStep === 1 && (
            <>
              <h3 className="text-xl font-bold text-foreground mb-4">Persoonlijke gegevens</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Voornaam <span className="text-error">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className={`w-full px-4 py-3 rounded-lg bg-input border ${
                      errors.firstName ? 'border-error' : 'border-border'
                    } text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-300`}
                    placeholder="Jan"
                  />
                  {errors.firstName && <p className="text-error text-xs mt-1">{errors.firstName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Achternaam <span className="text-error">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className={`w-full px-4 py-3 rounded-lg bg-input border ${
                      errors.lastName ? 'border-error' : 'border-border'
                    } text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-300`}
                    placeholder="de Vries"
                  />
                  {errors.lastName && <p className="text-error text-xs mt-1">{errors.lastName}</p>}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  E-mailadres <span className="text-error">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`w-full px-4 py-3 rounded-lg bg-input border ${
                    errors.email ? 'border-error' : 'border-border'
                  } text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-300`}
                  placeholder="jan.devries@bedrijf.nl"
                />
                {errors.email && <p className="text-error text-xs mt-1">{errors.email}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Telefoonnummer <span className="text-error">*</span>
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className={`w-full px-4 py-3 rounded-lg bg-input border ${
                    errors.phone ? 'border-error' : 'border-border'
                  } text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-300`}
                  placeholder="+31 6 12345678"
                />
                {errors.phone && <p className="text-error text-xs mt-1">{errors.phone}</p>}
              </div>
            </>
          )}

          {currentStep === 2 && (
            <>
              <h3 className="text-xl font-bold text-foreground mb-4">Bedrijfsinformatie</h3>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Bedrijfsnaam <span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => handleInputChange('company', e.target.value)}
                  className={`w-full px-4 py-3 rounded-lg bg-input border ${
                    errors.company ? 'border-error' : 'border-border'
                  } text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-300`}
                  placeholder="Uw Bedrijf B.V."
                />
                {errors.company && <p className="text-error text-xs mt-1">{errors.company}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Branche <span className="text-error">*</span>
                </label>
                <select
                  value={formData.industry}
                  onChange={(e) => handleInputChange('industry', e.target.value)}
                  className={`w-full px-4 py-3 rounded-lg bg-input border ${
                    errors.industry ? 'border-error' : 'border-border'
                  } text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-300`}
                >
                  <option value="">Selecteer een branche</option>
                  {industries.map((industry) => (
                    <option key={industry} value={industry}>
                      {industry}
                    </option>
                  ))}
                </select>
                {errors.industry && <p className="text-error text-xs mt-1">{errors.industry}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Aantal medewerkers <span className="text-error">*</span>
                </label>
                <select
                  value={formData.employees}
                  onChange={(e) => handleInputChange('employees', e.target.value)}
                  className={`w-full px-4 py-3 rounded-lg bg-input border ${
                    errors.employees ? 'border-error' : 'border-border'
                  } text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-300`}
                >
                  <option value="">Selecteer aantal medewerkers</option>
                  <option value="1-10">1-10 medewerkers</option>
                  <option value="11-50">11-50 medewerkers</option>
                  <option value="51-200">51-200 medewerkers</option>
                  <option value="201-500">201-500 medewerkers</option>
                  <option value="500+">500+ medewerkers</option>
                </select>
                {errors.employees && <p className="text-error text-xs mt-1">{errors.employees}</p>}
              </div>
            </>
          )}

          {currentStep === 3 && (
            <>
              <h3 className="text-xl font-bold text-foreground mb-4">Projectdetails</h3>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Gewenste dienst <span className="text-error">*</span>
                </label>
                <select
                  value={formData.service}
                  onChange={(e) => handleInputChange('service', e.target.value)}
                  className={`w-full px-4 py-3 rounded-lg bg-input border ${
                    errors.service ? 'border-error' : 'border-border'
                  } text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-300`}
                >
                  <option value="">Selecteer een dienst</option>
                  {services.map((service) => (
                    <option key={service} value={service}>
                      {service}
                    </option>
                  ))}
                </select>
                {errors.service && <p className="text-error text-xs mt-1">{errors.service}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Budget <span className="text-error">*</span>
                </label>
                <select
                  value={formData.budget}
                  onChange={(e) => handleInputChange('budget', e.target.value)}
                  className={`w-full px-4 py-3 rounded-lg bg-input border ${
                    errors.budget ? 'border-error' : 'border-border'
                  } text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-300`}
                >
                  <option value="">Selecteer een budget</option>
                  {budgetRanges.map((budget) => (
                    <option key={budget} value={budget}>
                      {budget}
                    </option>
                  ))}
                </select>
                {errors.budget && <p className="text-error text-xs mt-1">{errors.budget}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Gewenste tijdlijn <span className="text-error">*</span>
                </label>
                <select
                  value={formData.timeline}
                  onChange={(e) => handleInputChange('timeline', e.target.value)}
                  className={`w-full px-4 py-3 rounded-lg bg-input border ${
                    errors.timeline ? 'border-error' : 'border-border'
                  } text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-300`}
                >
                  <option value="">Selecteer een tijdlijn</option>
                  {timelines.map((timeline) => (
                    <option key={timeline} value={timeline}>
                      {timeline}
                    </option>
                  ))}
                </select>
                {errors.timeline && <p className="text-error text-xs mt-1">{errors.timeline}</p>}
              </div>
            </>
          )}

          {currentStep === 4 && (
            <>
              <h3 className="text-xl font-bold text-foreground mb-4">Uw bericht</h3>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Vertel ons over uw project <span className="text-error">*</span>
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => handleInputChange('message', e.target.value)}
                  rows={6}
                  className={`w-full px-4 py-3 rounded-lg bg-input border ${
                    errors.message ? 'border-error' : 'border-border'
                  } text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-300 resize-none`}
                  placeholder="Beschrijf uw uitdaging, doelen en wat u hoopt te bereiken met AI..."
                ></textarea>
                {errors.message && <p className="text-error text-xs mt-1">{errors.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Voorkeurscontactmethode
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {['E-mail', 'Telefoon', 'WhatsApp', 'Video call'].map((method) => (
                    <button
                      key={method}
                      type="button"
                      onClick={() => handleInputChange('preferredContact', method.toLowerCase())}
                      className={`px-4 py-3 rounded-lg border transition-all duration-300 ${
                        formData.preferredContact === method.toLowerCase()
                          ? 'bg-primary/20 border-primary text-primary' :'bg-input border-border text-foreground hover:border-primary/50'
                      }`}
                    >
                      {method}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="gdprConsent"
                  checked={formData.gdprConsent}
                  onChange={(e) => handleInputChange('gdprConsent', e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-border text-primary focus:ring-primary"
                />
                <label htmlFor="gdprConsent" className="text-sm text-muted-foreground">
                  Ik ga akkoord met de{' '}
                  <a href="#" className="text-primary hover:underline">
                    privacyverklaring
                  </a>{' '}
                  en geef toestemming voor het verwerken van mijn gegevens volgens de AVG/GDPR.{' '}
                  <span className="text-error">*</span>
                </label>
              </div>
              {errors.gdprConsent && <p className="text-error text-xs">{errors.gdprConsent as string}</p>}
            </>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 1}
            className="px-6 py-3 rounded-lg font-semibold text-foreground bg-muted hover:bg-muted/80 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
          >
            Vorige
          </button>
          {currentStep < totalSteps ? (
            <button
              onClick={handleNext}
              className="px-6 py-3 rounded-lg font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 shadow-silver-glow hover:shadow-orange-glow-hover card-3d-glow"
            >
              Volgende
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="px-6 py-3 rounded-lg font-semibold bg-cta text-cta-foreground hover:bg-cta/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-silver-glow hover:shadow-orange-glow-hover flex items-center space-x-2 button-3d"
            >
              {isSubmitting ? (
                <>
                  <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                  <span>Verzenden...</span>
                </>
              ) : (
                <span>Verstuur aanvraag</span>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactFormWizard;